import { useEffect, useRef, useState } from 'react';
import * as mediasoupClient from 'mediasoup-client';

// ICE servers (wire these to your coturn when ready)
const iceServers = [
  { urls: 'stun:stun.l.google.com:19302' },
  ...(import.meta.env.VITE_TURN_URL
    ? [{
        urls: import.meta.env.VITE_TURN_URL,
        username: import.meta.env.VITE_TURN_USERNAME || '',
        credential: import.meta.env.VITE_TURN_PASSWORD || '',
      }]
    : []),
];

// Signaling WS (Node server)
const SIGNALING_URL = import.meta.env.VITE_VCAFE_SIGNALING_URL || 'ws://localhost:5000';

/**
 * useVcafeWebRTC - mediasoup SFU hook for VirtualCafe
 * Exposes: { remoteStreams, connected, error }
 * Inputs:  { roomId, userId, localStream, micVolume }
 */
export const useVcafeWebRTC = ({ roomId, userId, localStream, micVolume = 1 }) => {
  const [remoteStreams, setRemoteStreams] = useState(new Map()); // userId -> MediaStream
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);
  const producersRef = useRef(new Map());           // kind -> producer
  const producerOwnersRef = useRef(new Map());      // producerId -> userId
  const consumersRef = useRef(new Map());           // consumerId -> { consumer, userId, producerId }
  const pendingProduceResolveRef = useRef(null);

  const audioContextRef = useRef(null);
  const micGainRef = useRef(null);
  const processedAudioTrackRef = useRef(null);

  const upsertRemoteTrack = (uid, track) => {
    if (!uid || !track) return;
    setRemoteStreams(prev => {
      const next = new Map(prev);
      const stream = next.get(uid) || new MediaStream();
      stream.getTracks().forEach(t => { if (t.kind === track.kind) stream.removeTrack(t); });
      stream.addTrack(track);
      next.set(uid, stream);
      return next;
    });
  };

  const cleanup = () => {
    sendTransportRef.current?.close();
    recvTransportRef.current?.close();
    producersRef.current.forEach(p => p.close());
    consumersRef.current.forEach(c => c.consumer.close());
    sendTransportRef.current = null;
    recvTransportRef.current = null;
    producersRef.current.clear();
    consumersRef.current.clear();
    producerOwnersRef.current.clear();
    setRemoteStreams(new Map());
    setConnected(false);
  };

  useEffect(() => {
    if (!roomId || !userId) return;

    const ws = new WebSocket(SIGNALING_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'vc-join', roomId, userId }));
    };

    ws.onmessage = async (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }

      switch (msg.type) {
        case 'vc-joined': {
          try {
            const device = new mediasoupClient.Device();
            await device.load({ routerRtpCapabilities: msg.routerRtpCapabilities });
            deviceRef.current = device;
            setConnected(true);
            ws.send(JSON.stringify({ type: 'vc-create-transport', roomId, userId, direction: 'send' }));
            ws.send(JSON.stringify({ type: 'vc-create-transport', roomId, userId, direction: 'recv' }));
            
            // Store existing producers for consumption after recv transport is ready
            if (msg.existingProducers && msg.existingProducers.length > 0) {
              ws._pendingExistingProducers = msg.existingProducers;
            }
          } catch (e) {
            setError('Failed to init mediasoup device');
            console.error(e);
          }
          break;
        }

        case 'vc-transport-created': {
          const { direction, params, transportId } = msg;
          const device = deviceRef.current;
          if (!device) return;

          if (direction === 'send' && !sendTransportRef.current) {
            const transport = device.createSendTransport({
              id: transportId,
              iceParameters: params.iceParameters,
              iceCandidates: params.iceCandidates,
              dtlsParameters: params.dtlsParameters,
              iceServers,
            });

            transport.on('connect', ({ dtlsParameters }, cb, eb) => {
              try {
                ws.send(JSON.stringify({ type: 'vc-connect-transport', roomId, userId, transportId: transport.id, dtlsParameters }));
                cb();
              } catch (e) { eb(e); }
            });

            transport.on('produce', async ({ kind, rtpParameters }, cb, eb) => {
              try {
                pendingProduceResolveRef.current = cb;
                ws.send(JSON.stringify({ type: 'vc-produce', roomId, userId, transportId: transport.id, kind, rtpParameters }));
              } catch (e) { eb(e); }
            });

            sendTransportRef.current = transport;

            if (localStream) {
              const audioTracks = localStream.getAudioTracks();
              const AudioCtx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);

              if (audioTracks.length > 0 && AudioCtx && !processedAudioTrackRef.current) {
                try {
                  const audioCtx = audioContextRef.current || new AudioCtx();
                  audioContextRef.current = audioCtx;
                  const source = audioCtx.createMediaStreamSource(new MediaStream([audioTracks[0]]));
                  const gainNode = audioCtx.createGain();
                  gainNode.gain.value = micVolume;
                  const dest = audioCtx.createMediaStreamDestination();
                  source.connect(gainNode).connect(dest);
                  micGainRef.current = gainNode;
                  const outTrack = dest.stream.getAudioTracks()[0];
                  processedAudioTrackRef.current = outTrack;
                } catch (e) {
                  console.warn('Failed to set up mic gain pipeline', e);
                  processedAudioTrackRef.current = null;
                }
              }

              localStream.getTracks().forEach(async track => {
                try {
                  if (track.kind === 'audio') {
                    if (producersRef.current.has('audio')) return;
                    const audioTrack = processedAudioTrackRef.current || track;
                    const producer = await transport.produce({ track: audioTrack });
                    producersRef.current.set('audio', producer);
                  } else {
                    if (producersRef.current.has(track.kind)) return;
                    const producer = await transport.produce({ track });
                    producersRef.current.set(track.kind, producer);
                  }
                } catch (e) { console.error('produce error', e); }
              });
            }
          }

          if (direction === 'recv' && !recvTransportRef.current) {
            const transport = device.createRecvTransport({
              id: transportId,
              iceParameters: params.iceParameters,
              iceCandidates: params.iceCandidates,
              dtlsParameters: params.dtlsParameters,
              iceServers,
            });

            transport.on('connect', ({ dtlsParameters }, cb, eb) => {
              try {
                ws.send(JSON.stringify({ type: 'vc-connect-transport', roomId, userId, transportId: transport.id, dtlsParameters }));
                cb();
              } catch (e) { eb(e); }
            });

            recvTransportRef.current = transport;
            
            // Consume existing producers (for late joiners)
            if (ws._pendingExistingProducers && ws._pendingExistingProducers.length > 0) {
              ws._pendingExistingProducers.forEach(({ producerId, peerId }) => {
                if (peerId === userId) return;
                producerOwnersRef.current.set(producerId, peerId);
                ws.send(JSON.stringify({
                  type: 'vc-consume',
                  roomId,
                  userId,
                  transportId: transport.id,
                  producerId,
                  rtpCapabilities: device.rtpCapabilities,
                }));
              });
              ws._pendingExistingProducers = null;
            }
          }
          break;
        }

        case 'vc-produced': {
          if (pendingProduceResolveRef.current) {
            try { pendingProduceResolveRef.current({ id: msg.producerId }); }
            finally { pendingProduceResolveRef.current = null; }
          }
          break;
        }

        case 'vc-new-producer': {
          const { userId: ownerUserId, producerId } = msg;
          if (!recvTransportRef.current || !deviceRef.current) return;
          if (ownerUserId === userId) return;
          producerOwnersRef.current.set(producerId, ownerUserId);
          ws.send(JSON.stringify({
            type: 'vc-consume',
            roomId,
            userId,
            transportId: recvTransportRef.current.id,
            producerId,
            rtpCapabilities: deviceRef.current.rtpCapabilities,
          }));
          break;
        }

        case 'vc-consumed': {
          const { consumerId, producerId, kind, rtpParameters } = msg;
          const recvTransport = recvTransportRef.current;
          if (!recvTransport) return;
          const ownerUserId = producerOwnersRef.current.get(producerId);
          try {
            const consumer = await recvTransport.consume({ id: consumerId, producerId, kind, rtpParameters });
            consumersRef.current.set(consumerId, { consumer, userId: ownerUserId, producerId });
            if (ownerUserId) upsertRemoteTrack(ownerUserId, consumer.track);
            try { await consumer.resume(); } catch (_) {}
          } catch (e) {
            console.error('consume error', e);
          }
          break;
        }

        case 'vc-peer-left': {
          const { userId: peerId } = msg;
          setRemoteStreams(prev => {
            const next = new Map(prev);
            next.delete(peerId);
            return next;
          });
          break;
        }

        case 'error': {
          setError(msg.error || 'Unknown signaling error');
          break;
        }

        default:
          break;
      }
    };

    ws.onerror = (error) => {
      console.error('[useWebRTC] WS error:', error);
      console.error('[useWebRTC] WS readyState:', ws.readyState);
      setError('WebSocket error');
    };
    ws.onclose = (event) => {
      console.log('[useWebRTC] WS closed. Code:', event.code, 'Reason:', event.reason, 'Clean:', event.wasClean);
      console.log('[useWebRTC] WS readyState at close:', ws.readyState);
      cleanup();
    };

    return () => {
      try { ws.send(JSON.stringify({ type: 'vc-leave' })); } catch (_) {}
      ws.close();
      cleanup();
    };
  }, [roomId, userId]);

  // If localStream appears after transports exist, start producing
  useEffect(() => {
    const sendTransport = sendTransportRef.current;
    if (!sendTransport || !localStream) return;
    const audioTracks = localStream.getAudioTracks();
    const AudioCtx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);

    if (audioTracks.length > 0 && AudioCtx && !processedAudioTrackRef.current) {
      try {
        const audioCtx = audioContextRef.current || new AudioCtx();
        audioContextRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(new MediaStream([audioTracks[0]]));
        const gainNode = audioCtx.createGain();
        gainNode.gain.value = micVolume;
        const dest = audioCtx.createMediaStreamDestination();
        source.connect(gainNode).connect(dest);
        micGainRef.current = gainNode;
        const outTrack = dest.stream.getAudioTracks()[0];
        processedAudioTrackRef.current = outTrack;
      } catch (e) {
        console.warn('Failed to set up mic gain pipeline (late)', e);
        processedAudioTrackRef.current = null;
      }
    }

    localStream.getTracks().forEach(async track => {
      if (track.kind === 'audio') {
        if (producersRef.current.has('audio')) return;
        try {
          const audioTrack = processedAudioTrackRef.current || track;
          const producer = await sendTransport.produce({ track: audioTrack });
          producersRef.current.set('audio', producer);
        } catch (e) { console.error('produce error', e); }
      } else {
        if (producersRef.current.has(track.kind)) return;
        try {
          const producer = await sendTransport.produce({ track });
          producersRef.current.set(track.kind, producer);
        } catch (e) { console.error('produce error', e); }
      }
    });
  }, [localStream]);

  // React to micVolume changes by updating gain node if present
  useEffect(() => {
    if (micGainRef.current) {
      micGainRef.current.gain.value = micVolume;
    }
  }, [micVolume]);

  return { remoteStreams, connected, error };
};
